export type ChequeBookRow = {
  id: string;
  bankAccountId: string;
  bankAccountLabel: string;
  serie: string;
  fromLeaf: string;
  toLeaf: string;
  branchName: string;
  createdAt: string;
};

export type RegisteredChequeRow = {
  id: string;
  /** شناسه دسته‌چک از `accounting_cheque_books_v1` */
  chequeBookId: string;
  /** شماره برگ داخل همان دسته */
  leafNumber: string;
  bankAccountLabel: string;
  serie: string;
  fullName: string;
  nationalCode: string;
  amount: number;
  dueDate: string;
  amountInWords: string;
  description: string;
  status: 'pending' | 'cleared' | 'bounced';
  createdAt: string;
};

export const STORAGE_KEY_BOOKS = 'accounting_cheque_books_v1';
export const STORAGE_KEY_REGISTERED = 'accounting_registered_cheques_v1';

function isLegacyCheque(raw: Record<string, unknown>): raw is {
  id: string;
  fullName: string;
  nationalCode: string;
  amount: number;
  dueDate: string;
  chequeNo?: string;
  description?: string;
  status: RegisteredChequeRow['status'];
  createdAt: string;
} {
  return typeof raw.chequeBookId !== 'string' && typeof raw.fullName === 'string';
}

function migrateRegisteredRow(raw: unknown): RegisteredChequeRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (
    typeof o.id === 'string' &&
    typeof o.chequeBookId === 'string' &&
    typeof o.leafNumber === 'string' &&
    typeof o.bankAccountLabel === 'string' &&
    typeof o.serie === 'string' &&
    typeof o.fullName === 'string' &&
    typeof o.nationalCode === 'string' &&
    typeof o.amount === 'number' &&
    typeof o.dueDate === 'string' &&
    typeof o.createdAt === 'string'
  ) {
    const status = o.status === 'cleared' || o.status === 'bounced' ? o.status : 'pending';
    return {
      id: o.id,
      chequeBookId: o.chequeBookId,
      leafNumber: o.leafNumber,
      bankAccountLabel: o.bankAccountLabel,
      serie: o.serie,
      fullName: o.fullName,
      nationalCode: String(o.nationalCode),
      amount: o.amount,
      dueDate: String(o.dueDate),
      amountInWords: typeof o.amountInWords === 'string' ? o.amountInWords : '',
      description: typeof o.description === 'string' ? o.description : '',
      status,
      createdAt: o.createdAt,
    };
  }
  if (isLegacyCheque(o)) {
    return {
      id: o.id,
      chequeBookId: '',
      leafNumber: typeof o.chequeNo === 'string' && o.chequeNo.trim() ? o.chequeNo.trim() : '-',
      bankAccountLabel: '— (ثبت قدیمی)',
      serie: '-',
      fullName: o.fullName,
      nationalCode: String(o.nationalCode),
      amount: o.amount,
      dueDate: o.dueDate,
      amountInWords: '',
      description: typeof o.description === 'string' ? o.description : '',
      status: o.status === 'cleared' || o.status === 'bounced' ? o.status : 'pending',
      createdAt: o.createdAt,
    };
  }
  return null;
}

export function readChequeBooks(): ChequeBookRow[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_BOOKS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ChequeBookRow[]) : [];
  } catch {
    return [];
  }
}

export function writeChequeBooks(rows: ChequeBookRow[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY_BOOKS, JSON.stringify(rows));
}

export function readRegisteredCheques(): RegisteredChequeRow[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_REGISTERED);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: RegisteredChequeRow[] = [];
    for (const item of parsed) {
      const row = migrateRegisteredRow(item);
      if (row) out.push(row);
    }
    return out;
  } catch {
    return [];
  }
}

export function writeRegisteredCheques(rows: RegisteredChequeRow[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY_REGISTERED, JSON.stringify(rows));
}

export function leafRangeInclusive(from: number, to: number): number[] {
  if (!Number.isFinite(from) || !Number.isFinite(to) || from > to) return [];
  const list: number[] = [];
  for (let n = from; n <= to; n += 1) list.push(n);
  return list;
}

export function usedLeafNumbers(bookId: string, cheques: RegisteredChequeRow[]): Set<number> {
  const used = new Set<number>();
  for (const c of cheques) {
    if (c.chequeBookId !== bookId) continue;
    const n = Number(c.leafNumber);
    if (Number.isFinite(n)) used.add(n);
  }
  return used;
}
